import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  getAppraisalCompliance,
  getAppraisalOffices,
  getAppraisalRatingScale,
  getAppraisalSchedule,
  getAppraisalSettings,
  saveAppraisalRatingScale,
  saveAppraisalSchedule,
  saveAppraisalSettings,
  startAppraisalCycle,
  stopAppraisalCycle,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { notifyAppraisalFeaturesChanged, normalizeAppraisalFeatures } from "./appraisalFeatures";
import "./Appraisal.css";

const FEATURE_SAVE_DEBOUNCE_MS = 350;

const emptySettings = {
  isRetrospective: false,
  isFeedback: true,
  isBehaviorGroup: true,
  isKpi: false,
  isDevelopment: false,
  isOneToOne: false,
  isOneToOneConversation: false,
  isPayIncrease: false,
  isLms: false,
};

const emptyPayIncreaseReadiness = {
  canEnable: false,
  hasComponents: false,
  hasStructures: false,
  blockedReason: "",
};

const defaultSchedule = {
  isFrequency: true,
  frequencyMonths: 12,
  isRemember: true,
  isActive: true,
  isAllOffices: true,
  officeId: null,
};

const FEATURE_FLAGS = [
  { key: "isBehaviorGroup", help: "groups" },
  { key: "isFeedback", help: "feedback" },
  { key: "isRetrospective", help: "retrospective" },
  { key: "isKpi", help: "goals" },
  { key: "isPayIncrease", help: "payIncrease" },
  { key: "isDevelopment", help: "pdp" },
  { key: "isLms", help: "lms" },
  { key: "isOneToOne", help: "oneToOne" },
  { key: "isOneToOneConversation", help: "conversations" },
];

/** English fallbacks if a locale is missing a key (i18n still prefers en.json via fallbackLng). */
const FEATURE_LABEL_FALLBACK = {
  isBehaviorGroup: "Behavior groups",
  isFeedback: "Feedback",
  isRetrospective: "Retrospective",
  isKpi: "KPIs / Goals",
  isPayIncrease: "Pay increase",
  isDevelopment: "Development / PDP",
  isLms: "LMS course links",
  isOneToOne: "One-to-one",
  isOneToOneConversation: "One-to-one conversations",
};

const FEATURE_HINT_FALLBACK = {
  isBehaviorGroup:
    "Not the same as Values & Behaviors. That menu is always available (core framework). This flag is for packaging different role checklists (groups) so people can get the right set of behaviors.",
  isFeedback: "Allow a closing narrative outcome on the appraisal, not only scores.",
  isRetrospective: "Reflection questions (what went well / differently) on the appraisal.",
  isKpi: "Track agreed outcomes and progress alongside behavioral ratings.",
  isPayIncrease:
    "Requires active Salary Components and a Published Payroll Structure. Manager recommends a raise → Pending Salary Increment — payroll activates it.",
  isDevelopment: "Personal development tasks with approval and completion tracking.",
  isLms: "Show admin-managed course name, description, and URL on My PDP.",
  isOneToOne: "Ongoing manager–employee check-ins outside the formal cycle.",
  isOneToOneConversation:
    "Structured check-ins with wellbeing, performance, and housekeeping prompts.",
};
const AppraisalSettingsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(emptySettings);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [ratingScale, setRatingScale] = useState({ behaviourRatingScale: 5, goalRatingScale: 5, retrospectiveRatingScale: 5 });
  const [compliance, setCompliance] = useState(null);
  const [offices, setOffices] = useState([]);
  const [cycleScopeAll, setCycleScopeAll] = useState(true);
  const [cycleOfficeId, setCycleOfficeId] = useState("");
  const [helpKey, setHelpKey] = useState(null);
  /** @type {[null | { step: 'start' | 'restart', scopeLabel: string, payload: object, warningMessage?: string, stats?: object }, Function]} */
  const [cycleConfirm, setCycleConfirm] = useState(null);
  const [cycleStarting, setCycleStarting] = useState(false);
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [cycleStopping, setCycleStopping] = useState(false);
  const [payIncreaseReadiness, setPayIncreaseReadiness] = useState(emptyPayIncreaseReadiness);
  const featureSaveTimerRef = useRef(null);
  const payIncreaseReadinessRef = useRef(payIncreaseReadiness);
  const latestFeatureSettingsRef = useRef(settings);
  payIncreaseReadinessRef.current = payIncreaseReadiness;

  const applySettingsFromApi = useCallback((rawSettings) => {
    const raw = rawSettings || {};
    const next = {
      ...emptySettings,
      id: raw.id ?? raw.Id,
      ...normalizeAppraisalFeatures(raw),
    };
    latestFeatureSettingsRef.current = next;
    setSettings(next);
    setPayIncreaseReadiness({
      canEnable: Boolean(raw.canEnablePayIncrease ?? raw.CanEnablePayIncrease),
      hasComponents: Boolean(raw.hasActiveSalaryComponents ?? raw.HasActiveSalaryComponents),
      hasStructures: Boolean(raw.hasPublishedPayrollStructures ?? raw.HasPublishedPayrollStructures),
      blockedReason: String(raw.payIncreaseBlockedReason ?? raw.PayIncreaseBlockedReason ?? ""),
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, sch, rs, c, officeList] = await Promise.all([
        getAppraisalSettings(),
        getAppraisalSchedule(),
        getAppraisalRatingScale(),
        getAppraisalCompliance(),
        getAppraisalOffices(),
      ]);
      applySettingsFromApi(s);
      const nextSchedule = { ...defaultSchedule, ...(sch || {}) };
      if (nextSchedule.officeId == null) nextSchedule.officeId = null;
      setSchedule(nextSchedule);
      setRatingScale({ behaviourRatingScale: 5, goalRatingScale: 5, retrospectiveRatingScale: 5, ...(rs || {}) });
      setCompliance(c || null);
      const officeOptions = Array.isArray(officeList) ? officeList : [];
      setOffices(officeOptions);
      if (!cycleOfficeId && officeOptions.length) {
        setCycleOfficeId(String(officeOptions[0].id));
      }
    } catch (e) {
      setError(e?.message || "Failed to load appraisal settings");
    } finally {
      setLoading(false);
    }
  }, [applySettingsFromApi, cycleOfficeId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (featureSaveTimerRef.current) clearTimeout(featureSaveTimerRef.current);
    },
    []
  );

  const persistFeatureSettings = useCallback(
    async (nextSettings) => {
      setSavingFeatures(true);
      setError("");
      try {
        const readiness = payIncreaseReadinessRef.current;
        if (nextSettings.isPayIncrease && !readiness.canEnable) {
          throw new Error(
            readiness.blockedReason ||
              t("app.appraisal.payIncreaseNeedsPayroll", {
                defaultValue:
                  "Set up Salary Components and a Published Payroll Structure before enabling Pay increase.",
              })
          );
        }
        const saved = await saveAppraisalSettings(nextSettings);
        // Another toggle happened while this request was in flight — save the latest snapshot.
        if (latestFeatureSettingsRef.current !== nextSettings) {
          if (featureSaveTimerRef.current) clearTimeout(featureSaveTimerRef.current);
          featureSaveTimerRef.current = setTimeout(() => {
            featureSaveTimerRef.current = null;
            void persistFeatureSettings(latestFeatureSettingsRef.current);
          }, FEATURE_SAVE_DEBOUNCE_MS);
          return;
        }
        applySettingsFromApi(saved || nextSettings);
        notifyAppraisalFeaturesChanged(saved || nextSettings);
        setMessage(
          t("app.appraisal.featuresSaved", {
            defaultValue: "Features updated.",
          })
        );
      } catch (e) {
        setError(e?.message || "Failed to update features");
        try {
          const s = await getAppraisalSettings();
          applySettingsFromApi(s);
          notifyAppraisalFeaturesChanged(s);
        } catch (_) {
          /* keep optimistic UI if reload also fails */
        }
      } finally {
        setSavingFeatures(false);
      }
    },
    [applySettingsFromApi, t]
  );

  const scheduleFeatureSave = useCallback(
    (nextSettings) => {
      latestFeatureSettingsRef.current = nextSettings;
      if (featureSaveTimerRef.current) clearTimeout(featureSaveTimerRef.current);
      featureSaveTimerRef.current = setTimeout(() => {
        featureSaveTimerRef.current = null;
        void persistFeatureSettings(latestFeatureSettingsRef.current);
      }, FEATURE_SAVE_DEBOUNCE_MS);
    },
    [persistFeatureSettings]
  );

  const onSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const schedulePayload = {
        ...schedule,
        isAllOffices: !!schedule.isAllOffices,
        officeId: schedule.isAllOffices ? null : Number(schedule.officeId) || null,
      };
      if (!schedulePayload.isAllOffices && !schedulePayload.officeId) {
        throw new Error("Select an office for the schedule, or choose all offices.");
      }
      await Promise.all([
        saveAppraisalSchedule(schedulePayload),
        saveAppraisalRatingScale(ratingScale),
      ]);
      setMessage(
        t("app.appraisal.saved", {
          defaultValue: "Schedule and rating scales saved.",
        })
      );
      await load();
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const closeCycleConfirm = () => {
    if (cycleStarting) return;
    setCycleConfirm(null);
  };

  const openStartCycleConfirm = () => {
    const scopeLabel = cycleScopeAll
      ? t("app.appraisal.allOffices", { defaultValue: "All company offices" })
      : offices.find((o) => String(o.id) === String(cycleOfficeId))?.name || "selected office";

    if (!cycleScopeAll && !cycleOfficeId) {
      setError("Select an office, or choose all offices.");
      return;
    }

    setError("");
    setMessage("");
    setCycleConfirm({
      step: "start",
      scopeLabel,
      payload: {
        isAllOffices: cycleScopeAll,
        officeId: cycleScopeAll ? null : Number(cycleOfficeId),
        confirmRestart: false,
      },
    });
  };

  const runStartCycle = async (payload, scopeLabel) => {
    const result = await startAppraisalCycle(payload);
    const needsConfirm = Boolean(result?.requiresConfirmation ?? result?.RequiresConfirmation);
    if (needsConfirm) {
      return {
        needsConfirm: true,
        warningMessage:
          result?.confirmationMessage ??
          result?.ConfirmationMessage ??
          t("app.appraisal.confirmRestartCycle", {
            defaultValue:
              "An appraisal cycle for this scope is still in progress. Starting again creates a new cycle. Continue?",
          }),
        stats: {
          pendingSelf: Number(result?.pendingSelfCount ?? result?.PendingSelfCount ?? 0),
          pendingManager: Number(result?.pendingManagerCount ?? result?.PendingManagerCount ?? 0),
          pendingMeeting: Number(result?.pendingMeetingCount ?? result?.PendingMeetingCount ?? 0),
          totalUsers: Number(result?.totalUsersInCycle ?? result?.TotalUsersInCycle ?? 0),
          meetingConfirmed: Number(result?.meetingConfirmedCount ?? result?.MeetingConfirmedCount ?? 0),
          existingDate: result?.existingAppraisalDate ?? result?.ExistingAppraisalDate ?? null,
          scope: result?.scopeLabel ?? result?.ScopeLabel ?? scopeLabel,
        },
      };
    }

    return {
      needsConfirm: false,
      result,
      scopeLabel: result?.scopeLabel || result?.ScopeLabel || scopeLabel,
      userCount: result?.userCount ?? result?.UserCount ?? 0,
    };
  };

  const confirmStartCycle = async () => {
    if (!cycleConfirm || cycleStarting) return;

    setCycleStarting(true);
    setError("");
    setMessage("");
    try {
      if (cycleConfirm.step === "start") {
        const outcome = await runStartCycle(cycleConfirm.payload, cycleConfirm.scopeLabel);
        if (outcome.needsConfirm) {
          setCycleConfirm({
            step: "restart",
            scopeLabel: cycleConfirm.scopeLabel,
            payload: { ...cycleConfirm.payload, confirmRestart: true },
            warningMessage: outcome.warningMessage,
            stats: outcome.stats,
          });
          return;
        }

        setCycleConfirm(null);
        setMessage(
          t("app.appraisal.cycleStartedScoped", {
            defaultValue: `Cycle started for ${outcome.userCount} employees (${outcome.scopeLabel}).`,
            count: outcome.userCount,
            scope: outcome.scopeLabel,
          })
        );
        await load();
        return;
      }

      // restart confirmed
      const outcome = await runStartCycle(
        { ...cycleConfirm.payload, confirmRestart: true },
        cycleConfirm.scopeLabel
      );
      if (outcome.needsConfirm) {
        setError("Cycle was not started. Confirmation is still required.");
        return;
      }

      setCycleConfirm(null);
      setMessage(
        t("app.appraisal.cycleStartedScoped", {
          defaultValue: `Cycle started for ${outcome.userCount} employees (${outcome.scopeLabel}).`,
          count: outcome.userCount,
          scope: outcome.scopeLabel,
        })
      );
      await load();
    } catch (e) {
      setError(e?.message || "Failed to start cycle");
      setCycleConfirm(null);
    } finally {
      setCycleStarting(false);
    }
  };

  const confirmStopCycle = async () => {
    if (cycleStopping) return;
    setCycleStopping(true);
    setError("");
    setMessage("");
    try {
      const result = await stopAppraisalCycle();
      const closed = result?.closedIncompleteCount ?? result?.ClosedIncompleteCount ?? 0;
      const kept = result?.keptCompletedCount ?? result?.KeptCompletedCount ?? 0;
      setMessage(
        t("app.appraisal.cycleStopped", {
          defaultValue:
            "Cycle stopped. {{closed}} unfinished appraisal(s) closed; {{kept}} completed kept. Retrospective and Feedback can be changed again.",
          closed,
          kept,
        })
      );
      setStopConfirmOpen(false);
      await load();
    } catch (e) {
      setError(e?.message || "Failed to stop cycle");
      setStopConfirmOpen(false);
    } finally {
      setCycleStopping(false);
    }
  };

  const hasIncompleteAppraisals = Boolean(
    compliance?.hasIncompleteAppraisals ?? compliance?.HasIncompleteAppraisals
  );

  const isFeatureLocked = (key) => {
    if (hasIncompleteAppraisals && (key === "isRetrospective" || key === "isFeedback")) return true;
    // Can always turn Pay increase off; only block turning it on without payroll setup.
    if (key === "isPayIncrease" && !settings.isPayIncrease && !payIncreaseReadiness.canEnable) return true;
    return false;
  };

  const toggle = (key) => {
    const locksMidCycle = key === "isRetrospective" || key === "isFeedback";
    if (locksMidCycle && hasIncompleteAppraisals) {
      setError(
        t("app.appraisal.featureLockedMidCycle", {
          defaultValue:
            "Cannot change Retrospective or Feedback while a cycle is in progress. Finish all Meeting Confirmed steps, or use Stop cycle, then change these flags for the next cycle.",
        })
      );
      return;
    }
    if (key === "isPayIncrease" && !settings.isPayIncrease && !payIncreaseReadiness.canEnable) {
      setError(
        payIncreaseReadiness.blockedReason || t("app.appraisal.payIncreaseNeedsPayroll")
      );
      return;
    }
    setError("");
    setMessage("");
    setSettings((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      scheduleFeatureSave(next);
      return next;
    });
  };

  return (
    <div className="appraisal-page appraisal-settings-page">
      <CommonPageHeader
        icon={<i className="bi bi-gear" />}
        title={t("app.appraisal.settingsTitle", { defaultValue: "Appraisal Settings" })}
        description={t("app.appraisal.settingsSubtitle", {
          defaultValue: "Configure cycles by office or company-wide, rating scales, and feature flags.",
        })}
        rightContent={
          <AppraisalGuideButton
            onClick={() => setHelpKey("settingsOverview")}
            label={t("app.appraisal.help.settingsGuideBtn", { defaultValue: "How settings work" })}
          />
        }
      />

      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <>
          <section className="appraisal-settings-panel">
            <div className="appraisal-settings-panel__head">
              <AppraisalSectionHeader
                icon="bi-graph-up"
                title={t("app.appraisal.compliance", { defaultValue: "Compliance" })}
                helpKey="startCycle"
                onOpenHelp={setHelpKey}
                tone="compliance"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.complianceHint", {
                  defaultValue:
                    "Snapshot of the latest cycle. Use Start appraisal cycle below when you are ready to open Self → Manager → Meeting for staff. If that scope still has unfinished appraisals, you will be warned before a new cycle is created.",
                })}
              </p>
              {compliance?.latestCompanyAppraisalId ? (
                <div className="appraisal-settings-meta">
                  <i className="bi bi-building" aria-hidden />
                  <span>
                    {t("app.appraisal.latestCycleScope", { defaultValue: "Latest cycle scope" })}:{" "}
                    <strong>
                      {compliance.isAllOffices
                        ? t("app.appraisal.allOffices", { defaultValue: "All offices" })
                        : compliance.officeName || `Office ${compliance.officeId}`}
                    </strong>
                  </span>
                </div>
              ) : null}
            </div>

            <div className="appraisal-kpi-grid appraisal-settings-stats" aria-label={t("app.appraisal.compliance", { defaultValue: "Compliance" })}>
              <div className="appraisal-kpi-card appraisal-kpi-card--neutral">
                <div className="appraisal-kpi-card__accent" aria-hidden />
                <div className="appraisal-kpi-card__inner">
                  <div className="appraisal-kpi-card__top">
                    <span className="appraisal-kpi-card__icon"><i className="bi bi-people" /></span>
                    <div className="appraisal-kpi-card__figures">
                      <span className="appraisal-kpi-card__value">{compliance?.totalUsers ?? 0}</span>
                      <span className="appraisal-kpi-card__label">{t("app.appraisal.totalUsers", { defaultValue: "Total users" })}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="appraisal-kpi-card appraisal-kpi-card--indigo">
                <div className="appraisal-kpi-card__accent" aria-hidden />
                <div className="appraisal-kpi-card__inner">
                  <div className="appraisal-kpi-card__top">
                    <span className="appraisal-kpi-card__icon"><i className="bi bi-person-check" /></span>
                    <div className="appraisal-kpi-card__figures">
                      <span className="appraisal-kpi-card__value">{compliance?.selfSubmittedCount ?? 0}</span>
                      <span className="appraisal-kpi-card__label">{t("app.appraisal.selfSubmitted", { defaultValue: "Self submitted" })}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="appraisal-kpi-card appraisal-kpi-card--green">
                <div className="appraisal-kpi-card__accent" aria-hidden />
                <div className="appraisal-kpi-card__inner">
                  <div className="appraisal-kpi-card__top">
                    <span className="appraisal-kpi-card__icon"><i className="bi bi-clipboard-check" /></span>
                    <div className="appraisal-kpi-card__figures">
                      <span className="appraisal-kpi-card__value">{compliance?.managerSubmittedCount ?? 0}</span>
                      <span className="appraisal-kpi-card__label">{t("app.appraisal.managerSubmitted", { defaultValue: "Manager submitted" })}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="appraisal-kpi-card appraisal-kpi-card--amber">
                <div className="appraisal-kpi-card__accent" aria-hidden />
                <div className="appraisal-kpi-card__inner">
                  <div className="appraisal-kpi-card__top">
                    <span className="appraisal-kpi-card__icon"><i className="bi bi-people-fill" /></span>
                    <div className="appraisal-kpi-card__figures">
                      <span className="appraisal-kpi-card__value">{compliance?.meetingConfirmedCount ?? 0}</span>
                      <span className="appraisal-kpi-card__label">{t("app.appraisal.meetingConfirmed", { defaultValue: "Meeting confirmed" })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="appraisal-settings-subpanel">
              <div className="appraisal-settings-subpanel__label">
                {t("app.appraisal.startCycleScope", { defaultValue: "Start cycle scope" })}
              </div>
              <div className="appraisal-scope-choice" role="radiogroup" aria-label={t("app.appraisal.startCycleScope", { defaultValue: "Start cycle scope" })}>
                <label className={`appraisal-scope-choice__option${cycleScopeAll ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    name="cycleScope"
                    checked={cycleScopeAll}
                    onChange={() => setCycleScopeAll(true)}
                  />
                  <i className="bi bi-buildings" aria-hidden />
                  <span>
                    <strong>{t("app.appraisal.allOffices", { defaultValue: "All company offices" })}</strong>
                    <small>{t("app.appraisal.allOfficesHint", { defaultValue: "Open Self → Manager → Meeting for everyone in scope." })}</small>
                  </span>
                </label>
                <label className={`appraisal-scope-choice__option${!cycleScopeAll ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    name="cycleScope"
                    checked={!cycleScopeAll}
                    onChange={() => setCycleScopeAll(false)}
                  />
                  <i className="bi bi-geo-alt" aria-hidden />
                  <span>
                    <strong>{t("app.appraisal.singleOffice", { defaultValue: "Specific office" })}</strong>
                    <small>{t("app.appraisal.singleOfficeHint", { defaultValue: "Start only for one office that is ready." })}</small>
                  </span>
                </label>
              </div>
              {!cycleScopeAll && (
                <div className="appraisal-scope-office">
                  <label className="appraisal-scope-office__label" htmlFor="appraisal-cycle-office">
                    {t("app.appraisal.office", { defaultValue: "Office" })}
                  </label>
                  <div className="appraisal-scope-office__control">
                    <i className="bi bi-building" aria-hidden />
                    <select
                      id="appraisal-cycle-office"
                      value={cycleOfficeId}
                      onChange={(e) => setCycleOfficeId(e.target.value)}
                    >
                      <option value="">{t("app.appraisal.selectOffice", { defaultValue: "Select office" })}</option>
                      {offices.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="appraisal-settings-cta">
                <div className="appraisal-settings-cta__copy">
                  <strong>{t("app.appraisal.startCycle", { defaultValue: "Start appraisal cycle" })}</strong>
                  <p>{t("app.appraisal.startCycleHint", { defaultValue: "Creates live appraisals for the selected scope." })}</p>
                </div>
                <div className="appraisal-settings-cta__actions">
                  {hasIncompleteAppraisals ? (
                    <button
                      type="button"
                      className="appraisal-btn appraisal-btn--stop"
                      onClick={() => setStopConfirmOpen(true)}
                      disabled={saving || cycleStarting || cycleStopping}
                    >
                      <i className="bi bi-stop-circle me-1" aria-hidden />
                      {t("app.appraisal.stopCycle", { defaultValue: "Stop cycle" })}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="appraisal-btn primary"
                    onClick={openStartCycleConfirm}
                    disabled={saving || cycleStarting || cycleStopping}
                  >
                    <i className="bi bi-play-circle me-1" aria-hidden />
                    {t("app.appraisal.startCycle", { defaultValue: "Start appraisal cycle" })}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="appraisal-settings-panel">
            <div className="appraisal-settings-panel__head">
              <AppraisalSectionHeader
                icon="bi-toggles"
                title={t("app.appraisal.featureFlags", { defaultValue: "Features" })}
                helpKey="features"
                onOpenHelp={setHelpKey}
                tone="settings"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.featuresHint", {
                  defaultValue:
                    "Turn modules on only when you will use them. Changes save automatically. Use What is this? for meaning, flow, and why each feature helps.",
                })}
              </p>
              {savingFeatures ? (
                <p className="appraisal-muted" style={{ marginTop: 0 }}>
                  {t("app.common.saving", { defaultValue: "Saving…" })}
                </p>
              ) : null}
            </div>
            {hasIncompleteAppraisals ? (
              <div className="appraisal-feature-callout appraisal-feature-callout--warn">
                <i className="bi bi-lock" aria-hidden />
                <div>
                  <strong>
                    {t("app.appraisal.featureMidCycleLockTitle", {
                      defaultValue: "Cycle in progress — some features are locked",
                    })}
                  </strong>
                  <p>
                    {t("app.appraisal.featureMidCycleLockBody", {
                      defaultValue:
                        "A cycle is already started. Retrospective and Feedback cannot be turned on or off until every appraisal reaches Meeting Confirmed, or you stop the cycle. Enable those features before the next cycle if you need them.",
                    })}
                  </p>
                  <button
                    type="button"
                    className="appraisal-btn appraisal-btn--stop"
                    onClick={() => setStopConfirmOpen(true)}
                    disabled={cycleStopping}
                  >
                    <i className="bi bi-stop-circle me-1" aria-hidden />
                    {t("app.appraisal.stopCycle", { defaultValue: "Stop cycle" })}
                  </button>
                </div>
              </div>
            ) : null}
            <div className="appraisal-feature-grid">
              {FEATURE_FLAGS.map((flag) => {
                const on = !!settings[flag.key];
                const locked = isFeatureLocked(flag.key);
                return (
                  <div
                    key={flag.key}
                    className={`appraisal-feature-card${on ? " is-on" : ""}${locked ? " is-locked" : ""}`}
                  >
                    <div className="appraisal-feature-card__top">
                      <label className="appraisal-feature-switch">
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={locked}
                          onChange={() => toggle(flag.key)}
                        />
                        <span className="appraisal-feature-switch__track" aria-hidden>
                          <span className="appraisal-feature-switch__thumb" />
                        </span>
                        <span className="appraisal-feature-card__title">
                          {t(`app.appraisal.feature.${flag.key}`, {
                            defaultValue: FEATURE_LABEL_FALLBACK[flag.key],
                          })}
                          {locked ? (
                            <i
                              className="bi bi-lock-fill"
                              title={
                                flag.key === "isPayIncrease"
                                  ? "Requires payroll setup"
                                  : "Locked while cycle is incomplete"
                              }
                              aria-hidden
                            />
                          ) : null}
                        </span>
                      </label>
                      <button
                        type="button"
                        className="appraisal-framework-info-btn"
                        onClick={() => setHelpKey(flag.help)}
                        title={t("app.appraisal.help.openTitle", { defaultValue: "What is this?" })}
                      >
                        <i className="bi bi-info-circle" aria-hidden />
                        <span>{t("app.appraisal.help.learn", { defaultValue: "What is this?" })}</span>
                      </button>
                    </div>
                    <p className="appraisal-feature-card__hint">
                      {t(`app.appraisal.featureHint.${flag.key}`, {
                        defaultValue: FEATURE_HINT_FALLBACK[flag.key],
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
            {!payIncreaseReadiness.canEnable ? (
              <div className="appraisal-feature-callout">
                <i className="bi bi-wallet2" aria-hidden />
                <div>
                  <strong>{t("app.appraisal.payIncreasePayrollGateTitle")}</strong>
                  <p>{t("app.appraisal.payIncreasePayrollGateBody")}</p>
                  <ul className="appraisal-readiness-list">
                    <li>
                      <span>{t("app.appraisal.payIncreasePayrollGateComponents")}</span>
                      <span className={`appraisal-readiness-pill${payIncreaseReadiness.hasComponents ? " is-ready" : " is-missing"}`}>
                        {payIncreaseReadiness.hasComponents
                          ? t("app.appraisal.payIncreasePayrollReady")
                          : t("app.appraisal.payIncreasePayrollMissing")}
                      </span>
                      <Link to="/payroll/salary-components">{t("app.appraisal.payIncreasePayrollLinkComponents")}</Link>
                    </li>
                    <li>
                      <span>{t("app.appraisal.payIncreasePayrollGateStructures")}</span>
                      <span className={`appraisal-readiness-pill${payIncreaseReadiness.hasStructures ? " is-ready" : " is-missing"}`}>
                        {payIncreaseReadiness.hasStructures
                          ? t("app.appraisal.payIncreasePayrollReady")
                          : t("app.appraisal.payIncreasePayrollMissing")}
                      </span>
                      <Link to="/payroll/payroll-structures">{t("app.appraisal.payIncreasePayrollLinkStructures")}</Link>
                    </li>
                  </ul>
                  {settings.isPayIncrease && !payIncreaseReadiness.canEnable ? (
                    <p className="appraisal-muted" style={{ marginBottom: 0, padding: 0 }}>
                      {t("app.appraisal.payIncreaseOnButPayrollMissing")}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
            {settings.isPayIncrease ? (
              <div className="appraisal-feature-callout">
                <i className="bi bi-info-circle" aria-hidden />
                <div>
                  <strong>{t("app.appraisal.payIncreaseCalloutTitle")}</strong>
                  <p>{t("app.appraisal.payIncreaseCalloutBody")}</p>
                  <button
                    type="button"
                    className="appraisal-framework-info-btn"
                    onClick={() => setHelpKey("payIncrease")}
                  >
                    <i className="bi bi-cash-coin" aria-hidden />
                    <span>{t("app.appraisal.help.learnPay")}</span>
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <div className="appraisal-settings-split">
            <section className="appraisal-settings-panel">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-calendar3"
                  title={t("app.appraisal.schedule", { defaultValue: "Schedule" })}
                  helpKey="schedule"
                  onOpenHelp={setHelpKey}
                  tone="schedule"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.scheduleHint", {
                    defaultValue: "Optional automatic cycle starts after a set number of months.",
                  })}
                </p>
              </div>

              <div className="appraisal-settings-form">
                <label className={`appraisal-feature-card appraisal-feature-card--compact${schedule.isFrequency ? " is-on" : ""}`}>
                  <span className="appraisal-feature-switch">
                    <input
                      type="checkbox"
                      checked={!!schedule.isFrequency}
                      onChange={() => setSchedule((p) => ({ ...p, isFrequency: !p.isFrequency }))}
                    />
                    <span className="appraisal-feature-switch__track" aria-hidden>
                      <span className="appraisal-feature-switch__thumb" />
                    </span>
                    <span className="appraisal-feature-card__title">
                      {t("app.appraisal.useFrequency", { defaultValue: "Use frequency (months)" })}
                    </span>
                  </span>
                </label>

                <div className="appraisal-field">
                  <label>{t("app.appraisal.frequencyMonths", { defaultValue: "Frequency months" })}</label>
                  <input
                    type="number"
                    min={1}
                    disabled={!schedule.isFrequency}
                    value={schedule.frequencyMonths ?? 12}
                    onChange={(e) => setSchedule((p) => ({ ...p, frequencyMonths: Number(e.target.value) || 12 }))}
                  />
                </div>

                <div className="appraisal-settings-subpanel__label">
                  {t("app.appraisal.scheduledCycleScope", { defaultValue: "Scheduled cycle scope" })}
                </div>
                <div className="appraisal-scope-choice appraisal-scope-choice--compact" role="radiogroup">
                  <label className={`appraisal-scope-choice__option${schedule.isAllOffices ? " is-active" : ""}`}>
                    <input
                      type="radio"
                      name="scheduleScope"
                      checked={!!schedule.isAllOffices}
                      onChange={() => setSchedule((p) => ({ ...p, isAllOffices: true, officeId: null }))}
                    />
                    <i className="bi bi-buildings" aria-hidden />
                    <span>
                      <strong>{t("app.appraisal.allOffices", { defaultValue: "All company offices" })}</strong>
                    </span>
                  </label>
                  <label className={`appraisal-scope-choice__option${!schedule.isAllOffices ? " is-active" : ""}`}>
                    <input
                      type="radio"
                      name="scheduleScope"
                      checked={!schedule.isAllOffices}
                      onChange={() =>
                        setSchedule((p) => ({
                          ...p,
                          isAllOffices: false,
                          officeId: p.officeId || offices[0]?.id || null,
                        }))
                      }
                    />
                    <i className="bi bi-geo-alt" aria-hidden />
                    <span>
                      <strong>{t("app.appraisal.singleOffice", { defaultValue: "Specific office" })}</strong>
                    </span>
                  </label>
                </div>

                {!schedule.isAllOffices && (
                  <div className="appraisal-scope-office">
                    <label className="appraisal-scope-office__label" htmlFor="appraisal-schedule-office">
                      {t("app.appraisal.office", { defaultValue: "Office" })}
                    </label>
                    <div className="appraisal-scope-office__control">
                      <i className="bi bi-building" aria-hidden />
                      <select
                        id="appraisal-schedule-office"
                        value={schedule.officeId ?? ""}
                        onChange={(e) =>
                          setSchedule((p) => ({
                            ...p,
                            officeId: e.target.value ? Number(e.target.value) : null,
                          }))
                        }
                      >
                        <option value="">{t("app.appraisal.selectOffice", { defaultValue: "Select office" })}</option>
                        {offices.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {schedule.lastRunDate ? (
                  <div className="appraisal-settings-meta">
                    <i className="bi bi-clock-history" aria-hidden />
                    <span>
                      {t("app.appraisal.lastAutomaticRun", { defaultValue: "Last automatic run" })}:{" "}
                      <strong>{new Date(schedule.lastRunDate).toLocaleString()}</strong>
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="appraisal-settings-panel">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-sliders"
                  title={t("app.appraisal.ratingScales", { defaultValue: "Rating scales" })}
                  helpKey="ratingScalesSettings"
                  onOpenHelp={setHelpKey}
                  tone="ratingScale"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.ratingScalesHint", {
                    defaultValue:
                      "Maximum scores for behaviors, goals, and retrospectives. Staff see these ranges (and labels) when they rate.",
                  })}
                </p>
              </div>

              <div className="appraisal-scale-grid">
                {[
                  {
                    key: "behavior",
                    label: t("app.appraisal.behaviorMax", { defaultValue: "Behavior max" }),
                    icon: "bi-list-check",
                    value: ratingScale.behaviourRatingScale ?? ratingScale.behaviorRatingScale ?? 5,
                    onChange: (n) =>
                      setRatingScale((p) => ({
                        ...p,
                        behaviourRatingScale: n,
                        behaviorRatingScale: n,
                      })),
                  },
                  {
                    key: "goal",
                    label: t("app.appraisal.goalMax", { defaultValue: "Goal max" }),
                    icon: "bi-bullseye",
                    value: ratingScale.goalRatingScale ?? 5,
                    onChange: (n) => setRatingScale((p) => ({ ...p, goalRatingScale: n })),
                  },
                  {
                    key: "retro",
                    label: t("app.appraisal.retrospectiveMax", { defaultValue: "Retrospective max" }),
                    icon: "bi-arrow-counterclockwise",
                    value: ratingScale.retrospectiveRatingScale ?? 5,
                    onChange: (n) => setRatingScale((p) => ({ ...p, retrospectiveRatingScale: n })),
                  },
                ].map((scale) => {
                  const max = Math.min(10, Math.max(1, Number(scale.value) || 5));
                  return (
                    <div key={scale.key} className="appraisal-scale-card">
                      <div className="appraisal-scale-card__head">
                        <span className="appraisal-scale-card__icon" aria-hidden>
                          <i className={`bi ${scale.icon}`} />
                        </span>
                        <label htmlFor={`appraisal-scale-${scale.key}`}>{scale.label}</label>
                      </div>
                      <div className="appraisal-scale-card__control">
                        <input
                          id={`appraisal-scale-${scale.key}`}
                          type="number"
                          min={1}
                          max={10}
                          value={max}
                          onChange={(e) => {
                            const n = Math.min(10, Math.max(1, Number(e.target.value) || 5));
                            scale.onChange(n);
                          }}
                        />
                        <span className="appraisal-scale-card__range">1 – {max}</span>
                      </div>
                      <div className="appraisal-scale-card__chips" aria-hidden>
                        {Array.from({ length: max }, (_, i) => (
                          <span key={i}>{i + 1}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="appraisal-settings-savebar">
            <p>
              {t("app.appraisal.saveSettingsHint", {
                defaultValue: "Save applies schedule and rating scales only. Feature switches save automatically.",
              })}
            </p>
            <button type="button" className="appraisal-btn primary" onClick={onSave} disabled={saving || savingFeatures}>
              {saving ? t("app.common.saving", { defaultValue: "Saving…" }) : t("app.common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </>
      )}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />

      {cycleConfirm ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-cycle-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeCycleConfirm();
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.settingsTitle", { defaultValue: "Appraisal Settings" })}
                </p>
                <h2 className="appraisal-modal__title" id="appraisal-cycle-confirm-title">
                  {cycleConfirm.step === "restart"
                    ? t("app.appraisal.restartCycleTitle", { defaultValue: "Cycle still in progress" })
                    : t("app.appraisal.startCycleTitle", { defaultValue: "Start appraisal cycle" })}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                aria-label={t("app.common.close", { defaultValue: "Close" })}
                disabled={cycleStarting}
                onClick={closeCycleConfirm}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i
                    className={`bi ${
                      cycleConfirm.step === "restart" ? "bi-exclamation-triangle" : "bi-play-circle"
                    }`}
                  />
                </div>
                <p className="appraisal-modal__lead">
                  {cycleConfirm.step === "restart"
                    ? cycleConfirm.warningMessage ||
                      t("app.appraisal.confirmRestartCycle", {
                        defaultValue:
                          "An appraisal cycle for this scope is still in progress. Starting again creates a new cycle.",
                      })
                    : t("app.appraisal.confirmStartCycleLead", {
                        defaultValue:
                          "This creates a live appraisal for each employee in the selected scope so they can complete Self → Manager → Meeting.",
                      })}
                </p>
              </div>

              <div className="appraisal-modal__target">
                <p className="appraisal-modal__target-label">
                  {t("app.appraisal.startCycleScope", { defaultValue: "Start cycle scope" })}
                </p>
                <p className="appraisal-modal__target-name">{cycleConfirm.scopeLabel}</p>
                {cycleConfirm.step === "restart" && cycleConfirm.stats ? (
                  <p className="appraisal-modal__target-meta">
                    {t("app.appraisal.restartCycleStats", {
                      defaultValue:
                        "Unfinished: {{self}} self, {{manager}} manager, {{meeting}} meeting (of {{total}}).",
                      self: cycleConfirm.stats.pendingSelf,
                      manager: cycleConfirm.stats.pendingManager,
                      meeting: cycleConfirm.stats.pendingMeeting,
                      total: cycleConfirm.stats.totalUsers,
                    })}
                  </p>
                ) : null}
              </div>

              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={cycleStarting}
                  onClick={closeCycleConfirm}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn primary"
                  disabled={cycleStarting}
                  onClick={confirmStartCycle}
                >
                  {cycleStarting
                    ? t("app.appraisal.startingCycle", { defaultValue: "Starting…" })
                    : cycleConfirm.step === "restart"
                      ? t("app.appraisal.startNewCycleAnyway", { defaultValue: "Start new cycle anyway" })
                      : t("app.appraisal.startCycleConfirmAction", { defaultValue: "Start cycle" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {stopConfirmOpen ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-stop-cycle-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !cycleStopping) setStopConfirmOpen(false);
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.settingsTitle", { defaultValue: "Appraisal Settings" })}
                </p>
                <h2 className="appraisal-modal__title" id="appraisal-stop-cycle-title">
                  {t("app.appraisal.stopCycleTitle", { defaultValue: "Stop appraisal cycle?" })}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                disabled={cycleStopping}
                onClick={() => setStopConfirmOpen(false)}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i className="bi bi-stop-circle" />
                </div>
                <p className="appraisal-modal__lead">
                  {t("app.appraisal.stopCycleLead", {
                    defaultValue:
                      "This closes unfinished appraisals in the latest cycle so staff can no longer continue them. Meeting Confirmed appraisals are kept. Retrospective and Feedback can then be changed again.",
                  })}
                </p>
              </div>
              <div className="appraisal-modal__target">
                <p className="appraisal-modal__target-label">
                  {t("app.appraisal.latestCycleScope", { defaultValue: "Latest cycle scope" })}
                </p>
                <p className="appraisal-modal__target-name">
                  {compliance?.isAllOffices ?? compliance?.IsAllOffices
                    ? t("app.appraisal.allOffices", { defaultValue: "All offices" })
                    : compliance?.officeName ||
                      compliance?.OfficeName ||
                      `Office ${compliance?.officeId ?? compliance?.OfficeId ?? ""}`}
                </p>
                <p className="appraisal-modal__target-meta">
                  {t("app.appraisal.stopCycleStats", {
                    defaultValue:
                      "Still open: {{self}} self, {{manager}} manager, {{meeting}} meeting (of {{total}}).",
                    self: compliance?.pendingSelfCount ?? compliance?.PendingSelfCount ?? 0,
                    manager: compliance?.pendingManagerCount ?? compliance?.PendingManagerCount ?? 0,
                    meeting: compliance?.pendingMeetingCount ?? compliance?.PendingMeetingCount ?? 0,
                    total: compliance?.totalUsers ?? compliance?.TotalUsers ?? 0,
                  })}
                </p>
              </div>
              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={cycleStopping}
                  onClick={() => setStopConfirmOpen(false)}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn appraisal-btn--stop"
                  disabled={cycleStopping}
                  onClick={confirmStopCycle}
                >
                  {cycleStopping
                    ? t("app.appraisal.stoppingCycle", { defaultValue: "Stopping…" })
                    : t("app.appraisal.stopCycleConfirmAction", { defaultValue: "Stop cycle" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AppraisalSettingsPage;
